/**
 * HeyGenAvatarPicker — sub-tabs UI: pick from "Gotowe" or "Twoje", or create a new custom avatar.
 *
 * Pure UI for the avatar slot in AdVideoBuilder. Owns:
 * - the avatar list (stock + ready custom merged on the server)
 * - the user's custom avatars (with status polling)
 * - the create form (Photo / Instant)
 *
 * Communicates the chosen `avatarId` upwards via `onSelect`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Upload, X, Trash2, Search, ZoomIn } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getAuthToken } from "@/lib/queryClient";

// ── Types ───────────────────────────────────────────────────

export interface HeyGenAvatar {
  id: string;
  avatar_name: string;
  preview_image_url: string;
  preview_video_url?: string;
  avatar_type: string;
}

export interface HeyGenCustomAvatar {
  id: string;
  name: string;
  type: "photo" | "instant";
  status: "uploading" | "training" | "ready" | "failed";
  heygenAvatarId: string | null;
  previewImageUrl: string | null;
  errorMessage: string | null;
}

interface ApiResponse<T> {
  message?: string;
}

type Tab = "ready" | "mine" | "create";

// ── Helpers ─────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return { Authorization: `Bearer ${token ?? ""}` };
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  const data = (await resp.json().catch(() => ({}))) as T & ApiResponse<T>;
  if (!resp.ok) throw new Error((data as ApiResponse<T>).message ?? `HTTP ${resp.status}`);
  return data;
}

const STATUS_LABEL_KEYS: Record<HeyGenCustomAvatar["status"], { key: string; className: string }> =
  {
    uploading: { key: "heygenAvatar.status.uploading", className: "text-yellow-400" },
    training: { key: "heygenAvatar.status.training", className: "text-blue-400" },
    ready: { key: "heygenAvatar.status.ready", className: "text-emerald-400" },
    failed: { key: "heygenAvatar.status.failed", className: "text-red-400" },
  };

// ── Component ───────────────────────────────────────────────

interface Props {
  selectedAvatarId: string;
  onSelect: (avatarId: string) => void;
}

export default function HeyGenAvatarPicker({ selectedAvatarId, onSelect }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("ready");
  const [avatars, setAvatars] = useState<HeyGenAvatar[]>([]);
  const [customAvatars, setCustomAvatars] = useState<HeyGenCustomAvatar[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadAvatars = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Initial + on-refresh load of merged avatar list
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch("/api/ad-creator/heygen/avatars", { headers: authHeaders() })
      .then((r) => jsonOrThrow<{ avatars: HeyGenAvatar[] }>(r))
      .then((res) => {
        if (cancelled) return;
        setAvatars(res.avatars ?? []);
        if (!selectedAvatarId && (res.avatars ?? []).length > 0) {
          onSelect(res.avatars[0].id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast({
          title: t("heygenAvatar.errors.fetch"),
          description: (err as Error).message,
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, onSelect, selectedAvatarId, toast]);

  // Load + poll custom avatars
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const resp = await fetch("/api/ad-creator/heygen/avatars/mine", { headers: authHeaders() });
        const data = await jsonOrThrow<{
          avatars: Array<HeyGenCustomAvatar & Record<string, unknown>>;
        }>(resp);
        if (cancelled) return;
        setCustomAvatars((data.avatars ?? []).map(normalizeCustomAvatar));
      } catch {
        // silent — avatars list still works
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Poll status for any avatar that's still training/uploading
  useEffect(() => {
    const pending = customAvatars.filter(
      (a) => a.status === "training" || a.status === "uploading"
    );
    if (pending.length === 0) return;

    let cancelled = false;
    const interval = setInterval(async () => {
      const refreshed = await Promise.all(
        pending.map((a) =>
          fetch(`/api/ad-creator/heygen/avatars/${a.id}/status`, { headers: authHeaders() })
            .then((r) => jsonOrThrow<{ avatar: HeyGenCustomAvatar & Record<string, unknown> }>(r))
            .then((d) => normalizeCustomAvatar(d.avatar))
            .catch(() => null)
        )
      );
      if (cancelled) return;

      let anyReady = false;
      setCustomAvatars((prev) => {
        const byId = new Map(prev.map((a) => [a.id, a]));
        for (const item of refreshed) {
          if (!item) continue;
          const previous = byId.get(item.id);
          if (previous && previous.status !== "ready" && item.status === "ready") anyReady = true;
          byId.set(item.id, item);
        }
        return Array.from(byId.values());
      });
      if (anyReady) reloadAvatars();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [customAvatars, reloadAvatars]);

  const handleCreated = useCallback(() => {
    setTab("mine");
    reloadAvatars();
  }, [reloadAvatars]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm(t("heygenAvatar.confirmDelete"))) return;
      try {
        const resp = await fetch(`/api/ad-creator/heygen/avatars/${id}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
        await jsonOrThrow(resp);
        setCustomAvatars((prev) => prev.filter((a) => a.id !== id));
        reloadAvatars();
      } catch (err) {
        toast({
          title: t("heygenAvatar.errors.delete"),
          description: (err as Error).message,
          variant: "destructive",
        });
      }
    },
    [reloadAvatars, toast, t]
  );

  const tabsConfig = useMemo<Array<{ id: Tab; label: string; count?: number }>>(
    () => [
      { id: "ready", label: t("heygenAvatar.tabs.ready"), count: avatars.length },
      { id: "mine", label: t("heygenAvatar.tabs.mine"), count: customAvatars.length },
      { id: "create", label: t("heygenAvatar.tabs.create") },
    ],
    [avatars.length, customAvatars.length, t]
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-2 border-b border-white/[0.08]">
        {tabsConfig.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "text-white border-purple-500"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            {t.label}
            {t.count != null && <span className="ml-1 text-white/30">({t.count})</span>}
          </button>
        ))}
      </div>

      {tab === "ready" && (
        <ReadyTab
          loading={loading}
          avatars={avatars}
          selected={selectedAvatarId}
          onSelect={onSelect}
        />
      )}

      {tab === "mine" && (
        <MineTab
          avatars={customAvatars}
          selected={selectedAvatarId}
          onSelect={onSelect}
          onDelete={handleDelete}
        />
      )}

      {tab === "create" && <CreateTab onCreated={handleCreated} />}
    </div>
  );
}

function normalizeCustomAvatar(
  raw: HeyGenCustomAvatar & Record<string, unknown>
): HeyGenCustomAvatar {
  return {
    id: String(raw.id),
    name: String(raw.name),
    type: raw.type === "instant" ? "instant" : "photo",
    status: (["uploading", "training", "ready", "failed"].includes(raw.status as string)
      ? raw.status
      : "training") as HeyGenCustomAvatar["status"],
    heygenAvatarId: (raw.heygenAvatarId as string | null) ?? null,
    previewImageUrl: (raw.previewImageUrl as string | null) ?? null,
    errorMessage: (raw.errorMessage as string | null) ?? null,
  };
}

// ── Avatar preview modal ────────────────────────────────────

function AvatarPreviewModal({
  avatar,
  onClose,
  onSelect,
  isSelected,
}: {
  avatar: HeyGenAvatar;
  onClose: () => void;
  onSelect: (id: string) => void;
  isSelected: boolean;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-lg w-full mx-4 rounded-2xl overflow-hidden border border-white/10 bg-[#0d0f14] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/60 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Media */}
        {avatar.preview_video_url ? (
          <video
            src={avatar.preview_video_url}
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-h-[60vh] object-contain bg-black"
          />
        ) : avatar.preview_image_url ? (
          <img
            src={avatar.preview_image_url}
            alt={avatar.avatar_name}
            className="w-full max-h-[60vh] object-contain bg-black"
          />
        ) : (
          <div className="w-full h-64 bg-white/[0.03] flex items-center justify-center text-white/30 text-sm">
            {t("heygenAvatar.noPreview")}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="text-sm font-medium text-white">{avatar.avatar_name}</div>
            <div className="text-[10px] text-white/40 mt-0.5">
              {avatar.avatar_type === "talking_photo"
                ? t("heygenAvatar.talkingPhoto")
                : t("heygenAvatar.stockAvatar")}
            </div>
          </div>
          <button
            onClick={() => {
              onSelect(avatar.id);
              onClose();
            }}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              isSelected
                ? "bg-purple-700 text-white/60 cursor-default"
                : "bg-purple-600 hover:bg-purple-500 text-white"
            }`}
          >
            {isSelected ? t("heygenAvatar.selected") : t("heygenAvatar.select")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────

const PAGE_SIZE = 40;

type TypeFilter = "all" | "stock_avatar" | "talking_photo";

const TYPE_FILTER_OPTIONS: Array<{ id: TypeFilter; labelKey: string }> = [
  { id: "all", labelKey: "heygenAvatar.filter.all" },
  { id: "stock_avatar", labelKey: "heygenAvatar.filter.stock" },
  { id: "talking_photo", labelKey: "heygenAvatar.filter.talkingPhoto" },
];

function ReadyTab({
  loading,
  avatars,
  selected,
  onSelect,
}: {
  loading: boolean;
  avatars: HeyGenAvatar[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [page, setPage] = useState(0);
  const [previewAvatar, setPreviewAvatar] = useState<HeyGenAvatar | null>(null);

  useEffect(() => {
    setPage(0);
  }, [query, typeFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return avatars.filter((a) => {
      if (typeFilter !== "all" && a.avatar_type !== typeFilter) return false;
      if (q && !a.avatar_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [avatars, query, typeFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading && avatars.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-4 text-xs text-white/40">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("heygenAvatar.loading")}
      </div>
    );
  }
  if (avatars.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-white/40 border border-dashed border-white/[0.12] rounded-lg">
        {t("heygenAvatar.emptyHint")}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Search + type filter row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("heygenAvatar.searchPlaceholder")}
            className="w-full pl-6 pr-2 py-1 text-xs bg-white/[0.04] border border-white/[0.08] rounded-md text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <div className="flex items-center gap-1">
          {TYPE_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setTypeFilter(opt.id)}
              className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                typeFilter === opt.id
                  ? "border-purple-500/60 bg-purple-500/10 text-white"
                  : "border-white/[0.08] text-white/40 hover:text-white/70"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-white/30 shrink-0">
          {filtered.length}/{avatars.length}
        </span>
      </div>

      {/* Avatar grid */}
      {paginated.length === 0 ? (
        <div className="px-3 py-4 text-xs text-white/40 text-center">
          {t("heygenAvatar.noResults", { query })}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
          {paginated.map((a) => (
            <div
              key={a.id}
              className={`group relative rounded-lg border overflow-hidden transition-all ${
                selected === a.id
                  ? "border-purple-500/60 ring-2 ring-purple-500/30"
                  : "border-white/[0.08] hover:border-white/20"
              }`}
            >
              <button
                onClick={() => onSelect(a.id)}
                className="w-full text-left"
                title={a.avatar_name}
              >
                {a.preview_image_url ? (
                  <img
                    src={a.preview_image_url}
                    alt={a.avatar_name}
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-square bg-white/[0.04] flex items-center justify-center text-[10px] text-white/30">
                    {t("heygenAvatar.noPreviewShort")}
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-black/70 text-[9px] text-white/80 truncate">
                  {a.avatar_name}
                </div>
              </button>
              {/* Zoom button — visible on hover */}
              <button
                onClick={() => setPreviewAvatar(a)}
                className="absolute top-1 right-1 p-1 rounded-md bg-black/70 text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title={t("heygenAvatar.previewTitle")}
              >
                <ZoomIn className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {previewAvatar && (
        <AvatarPreviewModal
          avatar={previewAvatar}
          onClose={() => setPreviewAvatar(null)}
          onSelect={onSelect}
          isSelected={selected === previewAvatar.id}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-0.5">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            className="px-2 py-1 text-[10px] border border-white/[0.08] rounded text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← {t("heygenAvatar.prev")}
          </button>
          <span className="text-[10px] text-white/30">
            {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages - 1}
            className="px-2 py-1 text-[10px] border border-white/[0.08] rounded text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {t("heygenAvatar.next")} →
          </button>
        </div>
      )}
    </div>
  );
}

function MineTab({
  avatars,
  selected,
  onSelect,
  onDelete,
}: {
  avatars: HeyGenCustomAvatar[];
  selected: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (avatars.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-white/40 border border-dashed border-white/[0.12] rounded-lg">
        {t("heygenAvatar.mineEmpty")}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {avatars.map((a) => {
        const status = STATUS_LABEL_KEYS[a.status];
        const isSelectable = a.status === "ready" && !!a.heygenAvatarId;
        const isSelected = isSelectable && a.heygenAvatarId === selected;
        return (
          <div
            key={a.id}
            className={`flex items-center gap-3 p-2 rounded-lg border ${
              isSelected ? "border-purple-500/60 ring-2 ring-purple-500/30" : "border-white/[0.08]"
            } bg-white/[0.02]`}
          >
            <div className="w-10 h-10 rounded-md bg-white/[0.05] overflow-hidden shrink-0">
              {a.previewImageUrl ? (
                <img src={a.previewImageUrl} alt={a.name} className="w-full h-full object-cover" />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white/80 truncate">{a.name}</div>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-white/40">
                  {a.type === "photo"
                    ? t("heygenAvatar.photoAvatar")
                    : t("heygenAvatar.instantAvatar")}
                </span>
                <span className={status.className}>· {t(status.key)}</span>
              </div>
              {a.errorMessage && (
                <div className="text-[10px] text-red-400/80 truncate">{a.errorMessage}</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {isSelectable && a.heygenAvatarId && (
                <Button
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  className="h-7 text-[11px]"
                  onClick={() => onSelect(a.heygenAvatarId!)}
                >
                  {isSelected ? t("heygenAvatar.selected") : t("heygenAvatar.use")}
                </Button>
              )}
              <button
                onClick={() => onDelete(a.id)}
                className="p-1.5 rounded text-white/30 hover:text-red-400"
                title={t("heygenAvatar.delete")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreateTab({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [type, setType] = useState<"photo" | "instant">("photo");
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept =
    type === "photo" ? "image/jpeg,image/png,image/webp" : "video/mp4,video/quicktime,video/webm";
  const maxMB = type === "photo" ? 15 : 100;
  const description =
    type === "photo" ? t("heygenAvatar.create.descPhoto") : t("heygenAvatar.create.descInstant");

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = "";
    setFile(f);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      toast({ title: t("heygenAvatar.create.errors.noName"), variant: "destructive" });
      return;
    }
    if (!file) {
      toast({ title: t("heygenAvatar.create.errors.noFile"), variant: "destructive" });
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      toast({
        title: t("heygenAvatar.create.errors.tooLarge"),
        description: t("heygenAvatar.create.maxSize", { mb: maxMB }),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("type", type);
      form.append("file", file);
      const resp = await fetch("/api/ad-creator/heygen/avatars/custom", {
        method: "POST",
        headers: authHeaders(),
        body: form,
      });
      await jsonOrThrow(resp);
      toast({
        title: t("heygenAvatar.create.successTitle"),
        description: t("heygenAvatar.create.successDesc"),
      });
      setName("");
      setFile(null);
      onCreated();
    } catch (err) {
      toast({
        title: t("heygenAvatar.create.errors.failed"),
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }, [name, file, type, maxMB, onCreated, toast, t]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {[
          {
            id: "photo" as const,
            labelKey: "heygenAvatar.create.photoLabel",
            descKey: "heygenAvatar.create.photoDesc",
          },
          {
            id: "instant" as const,
            labelKey: "heygenAvatar.create.instantLabel",
            descKey: "heygenAvatar.create.instantDesc",
          },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => {
              setType(opt.id);
              setFile(null);
            }}
            className={`px-3 py-2 rounded-lg border text-left transition-all ${
              type === opt.id
                ? "border-purple-500/60 bg-purple-500/10 text-white"
                : "border-white/[0.08] bg-white/[0.03] text-white/60 hover:border-white/20"
            }`}
          >
            <div className="text-xs font-medium">{t(opt.labelKey)}</div>
            <div className="text-[10px] text-white/40 mt-0.5">{t(opt.descKey)}</div>
          </button>
        ))}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-white/60">{t("heygenAvatar.create.nameLabel")}</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("heygenAvatar.create.namePlaceholder")}
          maxLength={60}
          className="h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-white/60">
          {type === "photo" ? t("heygenAvatar.create.portrait") : t("heygenAvatar.create.refVideo")}
        </Label>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
        />
        {file ? (
          <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.08] bg-white/[0.03]">
            <span className="text-xs text-white/70 truncate flex-1">{file.name}</span>
            <span className="text-[10px] text-white/40">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
            <button
              onClick={() => setFile(null)}
              className="p-0.5 rounded text-white/30 hover:text-red-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-dashed border-white/[0.12] text-xs text-white/40 hover:text-white/60 hover:border-white/20 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            {type === "photo"
              ? t("heygenAvatar.create.uploadPortrait")
              : t("heygenAvatar.create.uploadVideo")}
          </button>
        )}
        <p className="text-[10px] text-white/40">
          {description} {t("heygenAvatar.create.maxSize", { mb: maxMB })}.
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-purple-600 hover:bg-purple-500 text-white"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
        {t("heygenAvatar.create.submit")}
      </Button>
    </div>
  );
}
